// @ts-strict-ignore
import { ionHistory } from 'mobile_web/modules/navigation'
import { GetCollectionItemsIndexResponseData, GetScreensShowResponse } from 'mytours-read-api-typescript'
import { COLLECTION_ITEM_SUBTYPE_WEB, ScreenWebContainer as Container, RootStoreState } from 'mytours-read-common'
import React, { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import Iframe from 'react-iframe'
import Page from '../../common/components/Page'
import {
	containsEmbeddedURL,
	getDataFromLocalStorage,
	removeDataFromLocalStorage,
	setDataToLocalStorage,
	validateUrl,
} from '../../platform'
import { generateZipUrl, onLinkAction, renderScreenRedirect } from '../functions'
import './styles/screenWeb.less'
import usePrevious from 'common_lib/hooks/use-previous'
import { useLiveQuery } from 'dexie-react-hooks'
import AudioPlayerBarComponent from 'mobile_web/modules/audio/components/AudioPlayerBar'
import ErrorMessageHandler from 'mobile_web/modules/common/components/error-message-handler'
import db from 'mobile_web/modules/db'
import { getCurrentTimestamp } from 'mobile_web/modules/home/functions'
import NavigationService from 'mobile_web/modules/navigation/NavigationService'
import { useSelector } from 'react-redux'

interface LinkItem {
	id: string
	type: 'collection' | 'screen'
	subtype: 'list' | 'tour' | 'menu' | 'web' | 'story' | 'panorama'
}

interface StqryWindow {
	stqry?: {
		storage: {
			set: (changeset: Record<string, boolean>, callback: () => void, ctxKey?: string) => void
			get: (objectKeys: string[], callback: (changeset: Record<string, boolean>) => void, ctxKey?: string) => void
			remove: (objectKeys: string[], callback: () => void, ctxKey?: string) => void
		}
		linking: {
			openInternal: (params: LinkItem, callback?: () => void) => void
			openExternal: (link: string, callback?: () => void) => void
		}
		init: () => void
	}
}

interface StqryIFrame {
	readonly contentWindow: (WindowProxy & StqryWindow) | null
}

type IFrameElement = HTMLIFrameElement & StqryIFrame

interface OwnProps {
	isPageContent?: boolean
	isInsideModal?: boolean
}

type Props = Container.Props & Container.Actions & OwnProps

const initializedIFrameList: Record<string, boolean> = {}

const ScreenWeb: React.FC<Props> = props => {
	const [iFrame, setIFrame] = useState<IFrameElement>()

	const prevPropsItemId: number = usePrevious(props.itemId)

	const screen = useSelector((state: RootStoreState) => state.screen.screens.get(props.itemId)?.screen) || {}

	const earnedBadges = useLiveQuery(() => db.recentlyEarnedBadges.toArray())
	const trigger = useLiveQuery(
		() => db.triggers.where({ triggerable_id: props?.itemId, trigger_type: 'screen' }).first(),
		[props.itemId],
	)

	const [urlPageTypeResolvedUrl, setUrlPageTypeResolvedUrl] = useState('');
	useEffect(() => {
		async function resolveRedirects() {
			try {
				const response = await fetch(screen.url, { method: 'HEAD' });
				setUrlPageTypeResolvedUrl(response.url);
			} catch (error) {
				console.error('Failed to resolve URL:', error);
				setUrlPageTypeResolvedUrl(screen.url);
			}
		}
		if (screen.page_type === 'URL' && screen.url) {
			resolveRedirects();
		}
	}, [screen.url]);

	useEffect(() => {
		loadItem()
		waitForIFrame()

		return () => {
			// clear any error on Redux related to fetching screen from network
			// (so that the next time the user views this screen they are not immediately shown a previous error message)
			if (props.error) {
				props.clearError(props.itemId)
			}

			window.removeEventListener('message', receiveMessage, false)
		}
	}, [])

	useEffect(() => {
		if (trigger && earnedBadges) {
			const earned = !!earnedBadges.find(b => b.badge_id === trigger.badge_id)
			if (!earned) {
				props.toggleBadgeModal(true, trigger.badge_id, true, true)
			}
		}
	}, [props.itemId, trigger, earnedBadges])

	useEffect(() => {
		const { itemId } = props

		if (!screen || prevPropsItemId !== itemId) {
			loadItem()
		}
	}, [screen, props.itemId, props.itemVersion])

	useEffect(() => {
		waitForIFrame()
	}, [screen])

	useEffect(() => {
		updateIframe()
	}, [iFrame])

	const openModal = (linkItem: any): void => {
		const version = getCurrentTimestamp(true)
		NavigationService.pushModalWindow(linkItem.type, linkItem.subtype, linkItem.id, version)
	}

	const waitForIFrame = () => {
		if (!screen || screen?.page_type !== 'ZIP' || iFrame) {
			return
		}

		let attempt = 0
		const waitForIFrame = setInterval(() => {
			if (attempt === 10 || iFrame?.contentDocument) {
				clearInterval(waitForIFrame)
				return
			}

			try {
				if (document.getElementById(getZipIFrameId)) {
					setTimeout(() => {
						const newIFrame = document.getElementById(getZipIFrameId) as IFrameElement
						setIFrame(newIFrame)
					}, 1000)
					clearInterval(waitForIFrame)
				} else {
					attempt++
				}
			} catch (error) {
				console.error(error)
				clearInterval(waitForIFrame)
			}
		}, 500)
	}

	const callbackToIFrame = (callbackId: number, args: any[]) => {
		const message = {
			action: 'callback',
			version: 'v1',
			callbackId,
			args,
		}
		if (iFrame) {
			iFrame.contentWindow?.postMessage(JSON.stringify(message), '*')
		} else {
			console.error('iframe is not defined')
		}
	}

	const receiveMessage = async (event: MessageEvent) => {
		let message
		try {
			message = JSON.parse(event.data)
		} catch (error) {
			// here can be any extension, so the error isn't logged
		}

		if (!message) {
			return
		}

		console.log('receiveMessage', event)

		const { action, version, data, callbackId } = message
		if (version === 'v1' && action === 'linking.openInternal') {
			const { params } = data

			onLinkAction(params.subtype, Number(params.id), undefined, () => openModal(params))
		} else if (version === 'v1' && action === 'linking.openExternal') {
			const { link } = data
			const url = validateUrl(link)
			if (url) {
				window.open(link)
			}
		} else if (version === 'v1' && action === 'storage.set') {
			const { changeset, storageKey } = data

			const ids = (await getDataFromLocalStorage(storageKey, true)) || {}
			const value = { ...ids, ...changeset }
			await setDataToLocalStorage(storageKey, value, true)

			console.warn('Saving into storage:')
			console.log('[key]', storageKey)
			console.log('[value]', value)
			callbackToIFrame(callbackId, [])
		} else if (version === 'v1' && action === 'storage.get') {
			const { keys, storageKey } = data

			const storedData = (await getDataFromLocalStorage(storageKey, true)) || {}
			if (keys && Array.isArray(keys)) {
				Object.keys(storedData).forEach(key => {
					if (!keys.includes(key)) {
						delete storedData[key]
					}
				})
			}

			console.warn('Getting from storage:')
			console.log('[object keys]', keys)
			console.log('[key]', storageKey)
			console.log('[value]', storedData)

			callbackToIFrame(callbackId, [storedData])
		} else if (version === 'v1' && action === 'storage.remove') {
			const { keys, storageKey } = data

			if (keys && Array.isArray(keys)) {
				const ids = (await getDataFromLocalStorage(storageKey, true)) || {}

				keys.forEach(key => {
					delete ids[key]
				})

				await setDataToLocalStorage(storageKey, ids, true)
				console.warn('Removing from storage:')
				console.log('[object keys]', keys)
				console.log('[key]', storageKey)
			} else {
				await removeDataFromLocalStorage(storageKey, true)
				console.warn('Removing from storage:')
				console.log('[key]', storageKey)
			}

			callbackToIFrame(callbackId, [])
		} else if (version === 'v1' && action === 'navigation.back') {
			ionHistory.goBack()
		}
	}

	const setupStqry = (iFrameParam: IFrameElement) => {
		const message = {
			action: 'execute',
			version: 'v1',
			function: `(function() {
				window.stqryRuntime = "MobileWeb";
				window.stqry.loading = false;
			})()`,
		}

		if (iFrameParam) {
			iFrameParam.contentWindow?.postMessage(JSON.stringify(message), '*')
		}

		if (!initializedIFrameList[getZipIFrameId]) {
			window.addEventListener('message', receiveMessage, false)
		}

		initializedIFrameList[getZipIFrameId] = true
	}

	const loadItem = () => {
		const { itemId, itemVersion, language, accountId } = props

		if (!itemVersion) return

		props.loadItem(itemId, itemVersion, language, accountId)
	}

	const updateIframe = () => {
		const doc = iFrame?.contentDocument
		if (!doc) return

		const cssTag = screen.css ? `<style>${screen.css}</style>` : ''

		const headRegex = /<head.*?>([\s\S]*)<\/head>/
		const head = headRegex.exec(screen.html)
		if (head?.length > 1) {
			doc.head.innerHTML = head[1] + cssTag
		}

		if (!head && screen.css) {
			doc.head.innerHTML = cssTag
		}

		const bodyRegex = /<body.*?>([\s\S]*)<\/body>/
		const bodyHtml = bodyRegex.exec(screen.html)
		const bodyContent = bodyHtml?.length > 1 ? bodyHtml[1] : screen.html

		if (bodyContent) {
			doc.body.innerHTML = bodyContent
		}
	}

	const getZipIFrameId = useMemo<string>(() => `zip-iframe-${props.screen?.id}`, [props.screen])

	const renderScreen = (): JSX.Element => {
		if (!screen) {
			return null
		}

		const allow = `camera; magnetometer; gyroscope; accelerometer; geolocation self https://${
			ENV.BUNNY_UPLOADS_DOMAIN
		};`

		if (screen.page_type === 'URL' && screen.url) {
			const origin = urlPageTypeResolvedUrl;
			const urlPageTypeAllow = `camera self ${origin}; magnetometer self ${origin}; gyroscope self ${origin}; accelerometer self ${origin}; geolocation self ${origin};`
			return (
				urlPageTypeResolvedUrl ? <Iframe
					url={screen.url}
					width="100%"
					height="100%"
					className="webview-iframe"
					position="relative"
					title={(screen && screen.title) || ''}
					allow={`${urlPageTypeAllow} ${containsEmbeddedURL(screen.url) ? 'fullscreen' : ''}`}
				/> : null
			)
		} else if (screen.page_type === 'HTML' && screen.html) {
			return (
				<iframe
					ref={setIFrame}
					width="100%"
					height="100%"
					className="webview-iframe"
					title={screen?.title || ''}
					allow={allow}
				/>
			)
		} else if (screen.page_type === 'ZIP') {
			try {
				const url = generateZipUrl(String(screen.webpackage_media_item.file.id), screen.webpackage_media_item.file.region)

				return (
					<Iframe
						id={getZipIFrameId}
						url={url}
						width="100%"
						height="100%"
						className="webview-iframe"
						position="relative"
						title={screen?.title || ''}
						allow={allow}
						onLoad={() => {
							const newIFrame = document.getElementById(getZipIFrameId) as IFrameElement
							setIFrame(newIFrame)
							setupStqry(newIFrame)
						}}
					/>
				)
			} catch (error) {
				console.log('zip screen error', error)
				return null
			}
		} else {
			return null
		}
	}

	const renderContent = (): JSX.Element => {
		const { error } = props

		return (
			<>
				<ErrorMessageHandler error={error} />
				{!error && screen && renderScreen()}
			</>
		)
	}

	const { isPageContent, onBackAction, loading, isInsideModal } = props
	const { pathname } = ionHistory.location

	const isPageWithoutTabs = pathname.includes(COLLECTION_ITEM_SUBTYPE_WEB)

	const redirect = renderScreenRedirect(GetScreensShowResponse.ScreenTypeEnum.Web, screen?.screen_type)
	if (redirect) {
		return redirect
	}

	if (isPageContent) {
		return (
			<div
				data-item-id={String(props.itemId)}
				data-item-type="screen"
				data-item-subtype="story"
				style={{ width: '100%', height: '100%' }}
			>
				{renderContent()}
			</div>
		)
	} else {
		return (
			<>
				<Helmet
					title={screen?.title}
					meta={[
						{ name: 'description', content: screen?.title }, // TODO: add description field to web
						{ property: 'og:type', content: 'website' }, // TODO: check type
						{ property: 'og:title', content: screen?.title },
						{ property: 'og:description', content: screen?.title }, // TODO: add description field to web
						{ property: 'og:url', content: window.location.href },
					]}
				/>
				<Page
					headerProps={{
						title: screen?.title,
						onBackButton: onBackAction,
						hideBackButton: isInsideModal,
						showBackButton: Boolean(onBackAction),
						type: 'short',
						loading,

						collectionItem: null,
						itemType: GetCollectionItemsIndexResponseData.ItemTypeEnum.Screen,
						itemId: screen.id,
						collection: null,
						screen: screen,
						hideFavouriteButton: false,
					}}
					contentProps={{
						className: 'web-screen full-height',
						style: { '--content-background-image': 'url()' },
					}}
					footer={{
						component: !isInsideModal && <AudioPlayerBarComponent bottomOffset={isPageWithoutTabs ? 0 : -1} />,
					}}
					dataItemId={String(props.itemId)}
					dataItemType="screen"
					dataItemSubtype="web"
					dataCy="page-screen-web"
				>
					{renderContent()}
				</Page>
			</>
		)
	}
}

const ScreenWebComponent = Container.default(ScreenWeb)
export default ScreenWebComponent as any
